import React from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { formatElapsed } from '../../utils/formatUtils';

// Only standalone headings qualify; inline bold words in prose are not titles.
const extractReasoningHeadings = (text, streaming) => {
  const headings = [];
  const seen = new Set();
  const lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
  lines.forEach((line, index) => {
    const trimmed = line.trim();
    const markdownHeading = trimmed.match(/^#{1,6}\s+(.+?)(?:\s+#+)?$/);
    const boldHeading = trimmed.match(/^(\*\*|__)([^\n]+?)\1\s*$/);
    const partialHeading = streaming && index === lines.length - 1
      ? trimmed.match(/^(?:\*\*|__)([^*_\n]+)[*_]?$/)
      : null;
    const heading = (markdownHeading?.[1] || boldHeading?.[2] || partialHeading?.[1] || '')
      .replace(/^(\*\*|__)(.+)\1$/, '$2').trim();
    const key = heading.toLowerCase();
    if (heading && !seen.has(key)) { seen.add(key); headings.push(heading); }
  });
  return headings;
};

const ReasoningSection = ({
  message,
  showReasoning,
  thinkingSeconds,
  onToggleReasoning
}) => {
  if (!message.reasoning_text) return null;
  const isSummary = Boolean(message.reasoning_is_summary);
  const providerHeadings = extractReasoningHeadings(message.reasoning_text, message.streaming);
  const hasHeadings = providerHeadings.length > 0;
  const fragments = message.reasoning_text.split(/(\*\*[\s\S]+?\*\*|__[\s\S]+?__)/g);

  const getLabel = () => {
    if (message.streaming) {
      if (!message.text) {
        return isSummary
          ? `Reasoning ${formatElapsed(thinkingSeconds)}`
          : `Thinking ${formatElapsed(thinkingSeconds)}`;
      }
      return isSummary
        ? 'Reasoning summary'
        : `Thought for ${formatElapsed(thinkingSeconds)}`;
    }

    if (isSummary) {
      return message.reasoning_time
        ? `Reasoning summary in ${message.reasoning_time}`
        : 'Reasoning summary';
    }

    return message.reasoning_time ? `Thought for ${message.reasoning_time}` : 'Thinking';
  };

  return (
    <div className="mt-2 mb-2">
      <button
        type="button"
        aria-expanded={showReasoning}
        className="mb-2 flex items-center cursor-pointer text-sm text-gray-600 dark:text-gray-400"
        onClick={() => onToggleReasoning(message.id)}
      >
        <span className="mr-1">
          {getLabel()}
        </span>
        {showReasoning ? (
          <ChevronUp size={16} />
        ) : (
          <ChevronDown size={16} />
        )}
      </button>

      {showReasoning && (
        <div className="border-l-2 border-gray-300 dark:border-gray-600 pl-3 mt-1 text-xs text-gray-600 dark:text-gray-400 flex flex-col items-start gap-1.5 break-words"
          aria-label={hasHeadings ? "Reasoning headings" : "Reasoning text"}>
          {hasHeadings ? providerHeadings.map((heading, index) => (
            <span key={index} className={`font-semibold ${message.streaming && index === providerHeadings.length - 1 ? 'reasoning-shine-text' : ''}`}>
              {heading}
            </span>
          )) : (
            <span className={`whitespace-pre-wrap ${message.streaming ? 'reasoning-shine-text' : ''}`}>
              {fragments.map((fragment, index) => /^(\*\*|__)[\s\S]+?\1$/.test(fragment)
                ? <strong key={index}>{fragment.slice(2, -2)}</strong>
                : <React.Fragment key={index}>{fragment}</React.Fragment>)}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ReasoningSection;
